/**
 * Length-prefixed stdio framing shared by {@link ./worker-pool.js WorkerPool}
 * drivers.
 *
 * Each frame is a 4-byte big-endian unsigned length header followed by a UTF-8
 * JSON body of exactly that length. Length-prefixing (rather than
 * newline-delimited JSON) is deliberate: driver payloads can be large (snapshot
 * substrates ~90 KB, ffmpeg stderr) and arrive split across many stdio chunks,
 * and the JSON bodies themselves contain newlines — so a delimiter-based framer
 * would mis-split them.
 *
 * This lives in the worker-pool directory because a second driver (ffmpeg) now
 * needs it: the cpu-offload protocol re-exports these for back-compat. Per the
 * directory README's "promote on second use" rule.
 *
 * @module worker-pool/framing
 */

/**
 * Reserved id a worker emits once (as an ok response) when it has booted and
 * attached its stdin reader. The pool waits for it before sending real work,
 * which also surfaces a child that died during module load.
 */
export const READY_ID = 0;

const HEADER_BYTES = 4;
/**
 * Upper bound on a single frame body. Both ends are our own code, so this is a
 * corruption tripwire (a crashed child mid-write can leave bytes that decode as
 * a huge length) — not a real size limit. Exceeding it throws rather than letting
 * the decoder buffer toward an OOM.
 */
const MAX_FRAME_BYTES = 64 * 1024 * 1024;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Encode any JSON-serializable value as a single length-prefixed frame. */
export function encodeFrame(obj: unknown): Uint8Array {
  const body = encoder.encode(JSON.stringify(obj));
  const frame = new Uint8Array(HEADER_BYTES + body.length);
  new DataView(frame.buffer).setUint32(0, body.length, false);
  frame.set(body, HEADER_BYTES);
  return frame;
}

/**
 * Accumulates stdio chunks and yields whole decoded frames. Stateful: feed it
 * every chunk from a stream in order, and it returns the messages that became
 * complete on that push (often zero, sometimes several).
 */
export class FrameDecoder {
  private buf = new Uint8Array(0);

  /** Append a chunk and return every frame that is now complete. */
  push(chunk: Uint8Array): unknown[] {
    // Append. Reallocating per-chunk is fine for our message rate; the win in
    // clarity outweighs a ring buffer here.
    const next = new Uint8Array(this.buf.length + chunk.length);
    next.set(this.buf, 0);
    next.set(chunk, this.buf.length);
    this.buf = next;

    const out: unknown[] = [];
    let offset = 0;
    while (this.buf.length - offset >= HEADER_BYTES) {
      const len = new DataView(this.buf.buffer, this.buf.byteOffset + offset).getUint32(0, false);
      if (len > MAX_FRAME_BYTES) {
        throw new Error(`frame length ${len} exceeds ${MAX_FRAME_BYTES} (corrupt stream)`);
      }
      if (this.buf.length - offset - HEADER_BYTES < len) break; // body not fully arrived
      const start = offset + HEADER_BYTES;
      const body = this.buf.subarray(start, start + len);
      out.push(JSON.parse(decoder.decode(body)));
      offset = start + len;
    }
    // Advance past the consumed prefix. `subarray` is a view, so the consumed
    // bytes are not freed until the next push replaces `this.buf`.
    if (offset > 0) this.buf = this.buf.subarray(offset);
    return out;
  }
}
